import React, { useState } from "react";
import { ComposeType } from "types/communication.types";
import { CHANNEL_STYLES } from "utils/functions";

// ─── SVG icons ────────────────────────────────────────────────────────────────

const CloseIcon = () => (
  <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
    <path d="M9 2L2 9M2 2l7 7" stroke="#6b7280" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);

const ChevronIcon = ({ color }: { color: string }) => (
  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
    <path d="M2 4l3 3 3-3" stroke={color} strokeWidth="1.2" strokeLinecap="round" />
  </svg>
);

const AttachIcon = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
    <path
      d="M11.5 6L6 11.5C4.3 13.2 1.8 13.2.5 11.9-.3 11.1-.3 9.9.5 9l6-6c.8-.8 2-.8 2.5-.3s.5 1.7-.3 2.5L3.5 10.5C3.2 10.8 2.5 10.5 3 10L6.5 6.5"
      stroke="currentColor"
      strokeWidth="1.1"
      strokeLinecap="round"
    />
  </svg>
);

const SendIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path d="M11 6L1 11l1.5-5L1 1l10 5z" fill="white" />
  </svg>
);

// ─── FormRow ──────────────────────────────────────────────────────────────────

function FormRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        padding: "9px 0",
        borderBottom: "1px solid #f3f4f6",
        gap: 12,
      }}
    >
      <span style={{ fontSize: 12, color: "#9ca3af", width: 52, flexShrink: 0, fontWeight: 500 }}>{label}</span>
      {children}
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

type T_Props = {
  type: ComposeType;
  onClose: () => void;
};

const ComposeModal: React.FC<T_Props> = ({ type, onClose }) => {
  const [body, setBody] = useState("");

  if (!type) return null;

  const isEmail = type === "email";
  const ch = CHANNEL_STYLES[type];

  const headerBg = isEmail ? "#f5f3ff" : "#f0fdf4";
  const chipBg = isEmail ? "#eef2ff" : "#ecfdf5";
  const chipBorder = isEmail ? "#c7d2fe" : "#6ee7b7";
  const toPlaceholder = isEmail ? "customer@email.com" : "+1 (555) …";

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.32)",
          zIndex: 200,
          backdropFilter: "blur(3px)",
        }}
      />

      {/* Modal */}
      <div
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 560,
          background: "#fff",
          borderRadius: 16,
          zIndex: 201,
          boxShadow: "0 24px 64px rgba(0,0,0,0.18)",
          animation: "fadeUp 0.2s ease",
          overflow: "hidden",
        }}
      >
        {/* Modal header */}
        <div
          style={{
            padding: "14px 18px",
            borderBottom: "1px solid #f0f0f8",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: headerBg,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{ padding: "4px 12px", borderRadius: 20, background: chipBg, border: `1px solid ${chipBorder}` }}
            >
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: ch.color,
                  textTransform: "uppercase",
                  letterSpacing: "0.6px",
                }}
              >
                {ch.label}
              </span>
            </div>
            <span style={{ fontSize: 12.5, color: "#9ca3af" }}>New conversation</span>
          </div>
          <div
            onClick={onClose}
            style={{
              width: 28,
              height: 28,
              borderRadius: 6,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
          >
            <CloseIcon />
          </div>
        </div>

        {/* Form fields */}
        <div style={{ padding: "18px 20px 0" }}>
          {/* From */}
          <FormRow label="From">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "4px 10px",
                borderRadius: 7,
                background: chipBg,
                border: `1px solid ${chipBorder}`,
                cursor: "pointer",
              }}
            >
              <span style={{ fontSize: 12, fontWeight: 500, color: ch.color }}>{ch.handle}</span>
              <ChevronIcon color={ch.color} />
            </div>
          </FormRow>

          {/* To */}
          <FormRow label="To">
            <input
              type="text"
              placeholder={toPlaceholder}
              style={{
                flex: 1,
                fontSize: 13,
                color: "#111827",
                border: "none",
                background: "none",
                fontFamily: "inherit",
              }}
            />
          </FormRow>

          {/* Subject — email only */}
          {isEmail && (
            <FormRow label="Subject">
              <input
                type="text"
                placeholder="Subject…"
                style={{
                  flex: 1,
                  fontSize: 13,
                  color: "#111827",
                  border: "none",
                  background: "none",
                  fontFamily: "inherit",
                }}
              />
            </FormRow>
          )}

          {/* Body */}
          <textarea
            rows={6}
            placeholder="Write your message…"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            style={{
              width: "100%",
              fontSize: 13.5,
              color: "#111827",
              lineHeight: 1.6,
              marginTop: 14,
              border: "none",
              background: "#f9fafb",
              padding: "12px 14px",
              borderRadius: 10,
              display: "block",
            }}
          />
        </div>

        {/* Footer */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "12px 20px 16px",
          }}
        >
          {/* Toolbar — attach icon for email only */}
          <div style={{ display: "flex", gap: 4 }}>
            {isEmail && (
              <div
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 6,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  color: "#9ca3af",
                }}
              >
                <AttachIcon />
              </div>
            )}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button
              onClick={onClose}
              style={{
                padding: "8px 16px",
                border: "1px solid #e5e7eb",
                borderRadius: 8,
                background: "#fff",
                fontSize: 13,
                fontWeight: 500,
                color: "#374151",
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              Discard
            </button>
            <button
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "8px 20px",
                border: "none",
                borderRadius: 8,
                background: ch.color,
                color: "#fff",
                fontSize: 13,
                fontWeight: 500,
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              Send {ch.label} <SendIcon />
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ComposeModal;
